import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import SEO from './SEO';
import Navbar from './layout/Navbar';
import Footer from './layout/Footer';
import FloatingActions from './layout/FloatingActions';
import ImageSlider from "./ui/ImageSlider";
import { seoConfig } from '../data/seoData';
import { juniorFAQs } from '../data/faqsData';
import { juniorCurriculum } from '../data/coursesData';
import useFavicon from '../hooks/useFavicon';
import LazyImage from './ui/LazyImage';
import LazyVideo from './ui/LazyVideo';

// Bespoke premium rotating words animation for the Junior Academy Hero Heading
function RotatingHeading() {
  const words = ["Academic Foundations", "Conceptual Clarity", "Board Excellence", "Competitive Prep"];
  const [wordIdx, setWordIdx] = useState(0);
  const [subText, setSubText] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    let timer;
    const currentWord = words[wordIdx];
    const typingSpeed = isDeleting ? 40 : 85;

    if (isDeleting) {
      timer = setTimeout(() => {
        setSubText(currentWord.substring(0, subText.length - 1));
      }, typingSpeed);
    } else {
      timer = setTimeout(() => {
        setSubText(currentWord.substring(0, subText.length + 1));
      }, typingSpeed);
    }

    if (!isDeleting && subText === currentWord) {
      timer = setTimeout(() => setIsDeleting(true), 2200); // Pause on complete word
    } else if (isDeleting && subText === "") {
      setIsDeleting(false);
      setWordIdx((prev) => (prev + 1) % words.length);
    }

    return () => clearTimeout(timer);
  }, [subText, isDeleting, wordIdx]);

  return (
    <span className="inline-block relative py-1 bg-gradient-to-r from-[#7B5E27] via-[#A28038] to-[#0D243F] bg-clip-text text-transparent font-extrabold tracking-tight min-w-[150px] min-[360px]:min-w-[240px] sm:min-w-[360px] md:min-w-[480px] lg:min-w-[560px]">
      {subText}
      <span className="border-r-[3.5px] border-[#0D243F] ml-1.5 animate-pulse" style={{ verticalAlign: 'middle' }}></span>
    </span>
  );
}

export default function JuniorAcademy() {
  useFavicon('/junior academy-favicon/web-app-manifest-512x512.png');
  const navigate = useNavigate();
  const [activeFaq, setActiveFaq] = useState(null);
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768);
  const scrollRef = useRef(null);

  const scrollContainer = (direction) => {
    if (scrollRef.current) {
      const { scrollLeft, clientWidth } = scrollRef.current;
      const scrollAmount = clientWidth * 0.75;
      scrollRef.current.scrollTo({
        left: direction === 'left' ? scrollLeft - scrollAmount : scrollLeft + scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    handleResize();
    window.addEventListener("resize", handleResize, { passive: true });
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    let isHovered = false;
    const container = scrollRef.current;

    const handleMouseEnter = () => { isHovered = true; };
    const handleMouseLeave = () => { isHovered = false; };

    if (container) {
      container.addEventListener("mouseenter", handleMouseEnter, { passive: true });
      container.addEventListener("mouseleave", handleMouseLeave, { passive: true });
    }

    const timer = setInterval(() => {
      if (scrollRef.current && !isHovered) {
        const c = scrollRef.current;
        const maxScroll = c.scrollWidth - c.clientWidth;
        if (c.scrollLeft >= maxScroll - 10) {
          c.scrollTo({ left: 0, behavior: 'smooth' });
        } else {
          c.scrollBy({ left: 320, behavior: 'smooth' });
        }
      }
    }, 5200);

    return () => {
      clearInterval(timer);
      if (container) {
        container.removeEventListener("mouseenter", handleMouseEnter);
        container.removeEventListener("mouseleave", handleMouseLeave);
      }
    };
  }, []);

  const getSlug = (title) => title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

  const toggleFAQ = (index) => {
    setActiveFaq(activeFaq === index ? null : index);
  };

  // Reusable custom SVG drawings for visual storytelling
  const getSubjectIcon = (iconName) => {
    switch (iconName) {
      case "📚": // Class 10
        return (
          <svg className="w-8 h-8 text-[#C6A25E]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
          </svg>
        );
      case "🧪": // MPC
        return (
          <svg className="w-8 h-8 text-[#C6A25E]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 11.172V5L8 4z" />
          </svg>
        );
      case "🔬": // BiPC
        return (
          <svg className="w-8 h-8 text-[#C6A25E]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
        );
      case "🎯": // Entrance
        return (
          <svg className="w-8 h-8 text-[#C6A25E]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z" />
          </svg>
        );
      case "🏗️": // Foundation
        return (
          <svg className="w-8 h-8 text-[#C6A25E]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
          </svg>
        );
      default:
        return (
          <svg className="w-8 h-8 text-[#C6A25E]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
          </svg>
        );
    }
  };

  return (
    <React.Fragment>
      <SEO {...seoConfig.juniorAcademy} faqData={juniorFAQs} />

      <div className="font-inter bg-nm-light min-h-screen text-[#1E293B]">
        {/* Unified sticky nav header */}
        <Navbar />

        {/* Hero Section */}
        <section className="relative overflow-hidden w-full min-h-[90vh] flex items-center py-20 lg:py-24 bg-nm-light" aria-label="Hero Section">
          {/* SEAMLESS ASSETS BLENDING CONTAINER */}
          <div className="absolute inset-0 pointer-events-none z-0 overflow-hidden select-none">
            {/* Transparent Premium Soft Gradient Overlay */}
            <div className="absolute inset-0 bg-gradient-to-br from-[#FFFFFF]/60 via-[#F5FAFF]/40 to-[#F8F3EA]/20 backdrop-blur-[1px]" />

            {/* Highly Visible Background Video */}
            <LazyVideo
              src={isMobile ? "/videos/junior-mobile.mp4" : "/videos/junior-desktop.mp4"}
              poster="/logo-junior.webp"
              className="absolute inset-0 w-full h-full object-cover z-0 pointer-events-none select-none opacity-[0.30]"
              placeholderBg="bg-transparent"
            />

            {/* Soft Sky Blue / Gold Glows */}
            <div className="absolute top-0 right-1/4 w-[500px] h-[500px] bg-[#87CEEB]/10 rounded-full blur-[130px] animate-sunrise-shift" />
            <div className="absolute bottom-0 left-1/4 w-[400px] h-[400px] bg-[#C6A25E]/5 rounded-full blur-[120px] animate-pulse-slow" style={{ animationDelay: '4s' }} />
          </div>

          <div className="relative max-w-[1500px] mx-auto px-6 w-full flex flex-col items-center justify-center min-h-[80vh] relative z-20 py-20">
            {/* Centered Content Column */}
            <div className="relative z-20 text-center max-w-4xl mx-auto flex flex-col items-center animate-slide-up">
              {/* Header Badge */}
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
                <LazyImage
                  src="/junior academy-favicon/apple-touch-icon.png"
                  alt="Junior Academy Logo"
                  className="w-14 h-14 rounded-full object-cover border border-[#87CEEB]/30 shadow-lg bg-white"
                  fetchPriority="high"
                />
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass-card-light text-[#0B1C2D] text-xs font-semibold">
                  <span className="w-2 h-2 rounded-full bg-[#C6A25E] animate-pulse" />
                  <span>8th Class to Intermediate (1st & 2nd Year)</span>
                </div>
              </div>

              {/* Main Centered Heading */}
              <h1 className="font-playfair text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-bold leading-[1.08] text-[#0B1C2D] mb-8 tracking-tight">
                <span className="block">Build Strong</span>
                <span className="block mt-2"><RotatingHeading /></span>
              </h1>

              {/* Centered Description */}
              <p className="text-slate-600 text-base sm:text-lg md:text-xl leading-relaxed max-w-2xl mx-auto mb-10 font-light">
                Specialized coaching for school and intermediate students focused on conceptual clarity, board exam excellence, and competitive entrance preparation (EAPCET, NEET, IIT-JEE).
              </p>

              {/* Centered CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-16 w-full max-w-md">
                <a
                  href="/book-demo?vertical=junior"
                  className="px-8 py-4.5 rounded-2xl glass-button-light text-[#0B1C2D] font-bold text-center w-full sm:w-auto"
                >
                  Register Free 1-Day Demo
                </a>
                <a
                  href="#curriculum"
                  className="px-8 py-4.5 rounded-2xl glass-button-light text-[#C6A25E] font-bold text-center w-full sm:w-auto"
                >
                  Explore Programs
                </a>
              </div>

              {/* Centered Stats Tiles */}
              <div className="flex flex-wrap justify-center gap-4 sm:gap-6 w-full">
                {["JEE / NEET Prep", "Experienced Faculty", "Regular Evaluations"].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-3.5 glass-card-light px-5 py-3.5 rounded-2xl hover:scale-105 hover:-translate-y-1 transition-all duration-300 cursor-default">
                    <div className="w-9 h-9 rounded-full nm-inset-light flex items-center justify-center text-[#C6A25E] font-bold text-sm flex-shrink-0">✓</div>
                    <span className="text-sm font-semibold text-slate-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ================= RELATABLE MARQUEE loop ================= */}
        <section className="bg-nm-light border-y border-slate-200/40 py-8 relative overflow-hidden" aria-label="Junior Academy Trust Indicators">
          <div className="max-w-7xl mx-auto px-6 mb-4 text-center">
            <span className="text-xs font-bold text-[#C6A25E] uppercase tracking-widest">Our Academic Excellence Standards</span>
          </div>

          <div className="relative flex overflow-hidden w-full select-none py-2">
            {/* First Track */}
            <div className="flex shrink-0 gap-16 justify-around min-w-full animate-marquee-loop">
              {juniorMarquee.map((logo, idx) => (
                <div key={idx} className="flex items-center gap-3 text-slate-500 hover:text-[#0D243F] transition-colors duration-300">
                  <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                    {logo.svg}
                  </div>
                  <span className="text-sm font-semibold uppercase tracking-wider">{logo.name}</span>
                </div>
              ))}
            </div>
            {/* Second Track */}
            <div className="flex shrink-0 gap-16 justify-around min-w-full animate-marquee-loop" aria-hidden="true">
              {juniorMarquee.map((logo, idx) => (
                <div key={`dup-${idx}`} className="flex items-center gap-3 text-slate-500 hover:text-[#0D243F] transition-colors duration-300">
                  <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                    {logo.svg}
                  </div>
                  <span className="text-sm font-semibold uppercase tracking-wider">{logo.name}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Academic Programs Section */}
        <section id="curriculum" className="py-24 bg-nm-light border-t border-slate-200/40">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="font-playfair text-3xl md:text-5xl font-bold mb-4 text-[#0B1C2D]">
                Academic <span className="bg-gradient-to-r from-[#C6A25E] to-[#87CEEB] bg-clip-text text-transparent">Programs</span>
              </h2>
              <p className="text-slate-500 max-w-xl mx-auto text-base">
                Comprehensive coaching and foundation streams designed for academic excellence.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {juniorCurriculum.map((item, index) => (
                <div
                  key={index}
                  className="group nm-flat-light rounded-3xl border-transparent hover:-translate-y-1.5 transition-all duration-300 overflow-hidden flex flex-col justify-between h-full"
                >
                  <Link to={`/course/${getSlug(item.title)}`} className="relative aspect-video w-full overflow-hidden bg-slate-100 block">
                    <ImageSlider
                      images={item.images}
                      alt={item.title}
                      className="w-full h-full"
                    />
                  </Link>

                  <div className="p-6 flex-1 flex flex-col justify-between">
                    <div>
                      <span className="text-[10px] font-bold text-slate-400 tracking-wider uppercase">ACADEMIC PROGRAM</span>
                      <Link to={`/course/${getSlug(item.title)}`}>
                        <h3 className="text-xl font-bold text-[#0B1C2D] mt-1 mb-2 hover:text-[#C6A25E] transition-colors">{item.title}</h3>
                      </Link>
                      <p className="text-slate-500 text-sm leading-relaxed mb-6 font-light line-clamp-2">{item.description}</p>
                    </div>

                    <div>
                      <ul className="space-y-2 border-t border-slate-100 pt-4 mb-6">
                        {item.features.map((feature, idx) => (
                          <li key={idx} className="flex items-center text-slate-700 text-xs font-semibold">
                            <span className="w-1.5 h-1.5 rounded-full bg-[#C6A25E] mr-2 flex-shrink-0" />
                            {feature}
                          </li>
                        ))}
                      </ul>
                      <Link
                        to={`/course/${getSlug(item.title)}`}
                        className="w-full nm-button-light py-3 rounded-xl font-semibold border-transparent text-center block"
                      >
                        View Course Details
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Vision & Mission Section */}
        <section className="py-24 bg-nm-light border-t border-slate-200/40">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid md:grid-cols-2 gap-12 mb-20">
              <div className="nm-flat-light p-8 rounded-3xl border-transparent hover:scale-[1.01] transition-all duration-300">
                <div className="w-14 h-14 rounded-2xl nm-inset-light flex items-center justify-center text-[#C6A25E] text-2xl mb-6">👁️</div>
                <h3 className="text-xl font-bold mb-3 text-[#0B1C2D]">Our Vision</h3>
                <p className="text-slate-500 text-sm leading-relaxed font-light">
                  To nurture young minds from Class 10th through intermediate level, providing a solid foundation for academic excellence and competitive entrance exams. We envision a generation of students who excel academically with strong concepts that serve as the bedrock for their future.
                </p>
              </div>

              <div className="nm-flat-light p-8 rounded-3xl border-transparent hover:scale-[1.01] transition-all duration-300">
                <div className="w-14 h-14 rounded-2xl nm-inset-light flex items-center justify-center text-[#87CEEB] text-2xl mb-6">🎯</div>
                <h3 className="text-xl font-bold mb-3 text-[#0B1C2D]">Our Mission</h3>
                <p className="text-slate-500 text-sm leading-relaxed font-light">
                  Our mission is to provide comprehensive coaching and mentoring for school and college students. We prepare students for competitive exams like EAPCET through expert faculty, personalized attention, and proven methodologies that unlock critical analytical thinking.
                </p>
              </div>
            </div>

            {/* Founder message block */}
            <div className="nm-flat-light rounded-3xl p-8 md:p-12 border-transparent text-center max-w-4xl mx-auto relative overflow-hidden">
              <div className="absolute top-0 right-0 w-48 h-48 bg-gradient-to-br from-[#87CEEB]/10 to-transparent rounded-full blur-2xl pointer-events-none" />
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-gradient-to-tr from-[#C6A25E]/5 to-transparent rounded-full blur-2xl pointer-events-none" />

              <div className="relative z-10 flex flex-col items-center">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-[#C6A25E]/20 shadow-sm mb-6">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#C6A25E] animate-pulse" />
                  <span className="text-[10px] font-bold text-slate-600 uppercase tracking-widest">Founder's Message</span>
                </div>

                <div className="w-20 h-20 rounded-full overflow-hidden mb-6 border-2 border-[#C6A25E] shadow-md relative group">
                  <LazyImage src="/founder.webp" alt="Mohd. Yaasir Sufiyan" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                </div>

                <blockquote className="text-lg md:text-xl font-playfair font-medium text-[#0B1C2D] italic max-w-3xl leading-relaxed">
                  "Education is not just about passing exams—it's about building the foundation for a lifetime of learning. At Junior Academy, we focus on strengthening the core concepts during these crucial academic years, ensuring our students have the confidence to excel."
                </blockquote>

                <div className="mt-6">
                  <h4 className="text-base font-bold text-[#0B1C2D]">Mohd. Yaasir Sufiyan</h4>
                  <p className="text-xs font-bold text-[#C6A25E] uppercase tracking-widest mt-1">Founder, Cervello Eduverse</p>
                </div>
              </div>
            </div>
          </div>
        </section>
        {/* Cervello Junior Academy Testimonials Section */}
        <section className="py-24 bg-nm-light border-t border-slate-200/40 overflow-hidden">
          <style dangerouslySetInnerHTML={{
            __html: `
            .scrollbar-none::-webkit-scrollbar {
              display: none;
            }
          `}} />
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex flex-col md:flex-row md:justify-between md:items-end gap-6 mb-12">
              <div>
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block mb-4">Achievements</span>
                <h2 className="font-playfair text-3xl md:text-5xl font-bold mb-4 text-[#0B1C2D]">
                  Cervello Junior Academy <span className="bg-gradient-to-r from-[#C6A25E] to-[#87CEEB] bg-clip-text text-transparent">Success Stories</span>
                </h2>
                <p className="text-slate-500 max-w-xl text-sm sm:text-base">
                  Real feedback and achievements by our intermediate students in board and entrance examinations.
                </p>
              </div>

              {/* Scroll controls */}
              <div className="flex gap-3 self-start md:self-end">
                <button
                  onClick={() => scrollContainer('left')}
                  className="w-12 h-12 rounded-xl nm-button-light flex items-center justify-center border-transparent cursor-pointer focus:outline-none"
                  aria-label="Scroll left"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <button
                  onClick={() => scrollContainer('right')}
                  className="w-12 h-12 rounded-xl nm-button-light text-[#C6A25E] flex items-center justify-center border-transparent cursor-pointer focus:outline-none"
                  aria-label="Scroll right"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="relative">
              {/* Horizontal Scroll Track */}
              <div
                ref={scrollRef}
                className="flex gap-6 overflow-x-auto pb-8 pt-4 px-2 snap-x snap-mandatory scrollbar-none scroll-smooth -mx-6 md:-mx-12"
                style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
              >
                <div className="flex-shrink-0 w-4 md:w-10" />
                {[
                  {
                    name: "Mohd Uzair",
                    stream: "MPC Stream",
                    achievement: "EAPCET 2026: 26,000 Rank",
                    avatar: "/avatar-normal-boy.webp",
                    text: "The conceptual clarity I got here for mathematics and physics made a difference. The focus on solving previous years' questions helped me secure a 26,000 rank in EAPCET!"
                  },
                  {
                    name: "Simran Fatima",
                    stream: "BiPC (EAPCET Batch)",
                    achievement: "EAPCET 2026: 29,197 Rank",
                    avatar: "/avatar-hijab-girl.webp",
                    text: "Being part of the EAPCET batch was amazing. The focused study planner and interactive biology sessions helped me secure a 29,197 rank in EAPCET!"
                  },
                  {
                    name: "Ayesha",
                    stream: "BiPC Stream – 1st Year",
                    achievement: "95% Board Score (2025-2026)",
                    avatar: "/avatar-hijab-girl.webp",
                    text: "The conceptual approach to Zoology and Botany is what helped me score a massive 95% in my 1st Year BiPC boards! Best intermediate coaching ever."
                  },
                  {
                    name: "Usama",
                    stream: "MPC Stream – 1st Year",
                    achievement: "80% Board Score (2025-2026)",
                    avatar: "/avatar-normal-boy.webp",
                    text: "The conceptual clarity in Mathematics and Physics made learning so interesting. Getting 80% in my MPC 1st Year boards is a great start, all thanks to Cervello Junior Academy!"
                  },
                  {
                    name: "Ayesha Simra Aleem",
                    stream: "BiPC Stream – 1st Year",
                    achievement: "88% Board Score (2025-2026)",
                    avatar: "/avatar-hijab-girl.webp",
                    text: "Excellent faculty and continuous evaluation. Scoring 88% in my BiPC 1st Year boards has given me so much confidence for EAPCET preparation."
                  },
                  {
                    name: "Samreen",
                    stream: "MPC Stream – 2nd Year",
                    achievement: "70% Board Score (2025-2026)",
                    program: "Free Board Support Program",
                    avatar: "/avatar-hijab-girl.webp",
                    text: "I was part of the free board support program, and it helped me prepare thoroughly for the intermediate exams. Scoring 70% in my MPC 2nd Year boards was a huge relief and achievement."
                  },
                  {
                    name: "Mohd Imran",
                    stream: "MPC Stream – 2nd Year",
                    achievement: "70% Boards (25-26) | 60K EAPCET (2026)",
                    program: "Both Boards & EAPCET Batch",
                    avatar: "/avatar-normal-boy.webp",
                    text: "Being in both the board preparation and EAPCET batch was highly beneficial. The parallel test series helped me score 70% in my 2nd-year boards and get a 60,000 EAPCET rank!"
                  },
                  {
                    name: "Jasmine Sultana",
                    stream: "BiPC Stream",
                    achievement: "EAPCET 2026: 54,276 Rank",
                    avatar: "/avatar-hijab-girl.webp",
                    text: "The practice mocks and regular feedback sessions gave me the confidence I needed. Securing a 54,276 rank in EAPCET is a huge milestone for me, thanks to Junior Academy!"
                  },
                  {
                    name: "Syed Bilal",
                    stream: "Class 10th",
                    achievement: "9.8 GPA Board Score (2025-2026)",
                    avatar: "/avatar-normal-boy.webp",
                    text: "The conceptual understanding of Mathematics and Physics was key to my success. Scoring a 9.8 GPA in my SSC Board exams was a proud moment for me and my family, thanks to Cervello Junior Academy!"
                  },
                  {
                    name: "Fatima Begum",
                    stream: "Class 10th",
                    achievement: "9.5 GPA Board Score (2025-2026)",
                    avatar: "/avatar-hijab-girl.webp",
                    text: "Cervello Junior Academy helped me clear all my science doubts. I scored a 9.5 GPA in the 10th class boards. Highly recommend the foundation batch!"
                  }
                ].map((student, idx) => (
                  <div key={idx} className="snap-start flex-shrink-0 w-[290px] sm:w-[320px] md:w-[350px] nm-flat-light rounded-3xl p-6 border-transparent hover:-translate-y-1.5 transition-all duration-300 flex flex-col justify-between">
                    <div>
                      <div className="flex items-center gap-3.5 mb-5">
                        <div className="w-12 h-12 rounded-full overflow-hidden nm-inset-light flex-shrink-0 shadow-sm flex items-center justify-center">
                          <LazyImage src={student.avatar} alt={student.name} className="w-full h-full object-cover" />
                        </div>
                        <div>
                          <h4 className="font-bold text-[#0B1C2D] text-sm">{student.name}</h4>
                          <div className="flex flex-col gap-0.5 mt-0.5">
                            <span className="text-[10px] font-bold text-slate-400 uppercase">{student.stream}</span>
                            {student.program && (
                              <span className="text-[9px] font-semibold text-[#87CEEB] uppercase">{student.program}</span>
                            )}
                          </div>
                        </div>
                      </div>
                      <p className="text-slate-600 text-xs sm:text-sm leading-relaxed font-light italic mb-6">
                        "{student.text}"
                      </p>
                    </div>
                    <div className="border-t border-slate-100 pt-4 flex justify-between items-center">
                      <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Outcome</span>
                      <span className="text-xs font-bold text-[#C6A25E] nm-inset-light px-2.5 py-1 rounded-full">{student.achievement}</span>
                    </div>
                  </div>
                ))}

                {/* View All Stories Card at the end */}
                <Link
                  to="/testimonials"
                  className="snap-start flex-shrink-0 w-[290px] sm:w-[320px] md:w-[350px] nm-flat-light rounded-3xl p-8 border-transparent hover:nm-inset-light transition-all duration-300 flex flex-col items-center justify-center text-center cursor-pointer group"
                >
                  <div className="w-16 h-16 rounded-full nm-inset-light text-[#C6A25E] flex items-center justify-center text-2xl mb-4 group-hover:scale-110 transition-all duration-300">
                    <svg className="w-6 h-6 text-[#C6A25E]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                    </svg>
                  </div>
                  <h3 className="font-playfair text-xl font-bold text-[#0B1C2D] mb-2">View All Success Stories</h3>
                  <p className="text-slate-500 text-xs sm:text-sm font-light max-w-[240px] leading-relaxed">
                    Browse complete student testimonials, ranks, and board exam scores
                  </p>
                  <span className="mt-6 text-xs font-bold text-[#C6A25E] flex items-center gap-1 group-hover:gap-2 transition-all">
                    <span>Explore testimonials</span>
                    <span className="text-sm font-light">→</span>
                  </span>
                </Link>
                <div className="flex-shrink-0 w-4 md:w-10" />
              </div>
            </div>
          </div>
        </section>

        {/* FAQs */}
        <section className="py-24 bg-nm-light border-t border-slate-200/40">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="font-playfair text-3xl md:text-5xl font-bold mb-4 text-[#0B1C2D]">
                Junior Academy <span className="bg-gradient-to-r from-[#C6A25E] to-[#87CEEB] bg-clip-text text-transparent">FAQ</span>
              </h2>
              <p className="text-slate-500 max-w-xl mx-auto text-base">
                Common questions about our school foundation and intermediate coaching programs.
              </p>
            </div>

            <div className="max-w-3xl mx-auto space-y-4">
              {juniorFAQs.map((faq, index) => {
                const isOpen = activeFaq === index;
                return (
                  <div key={index} className="nm-flat-light rounded-2xl border-transparent overflow-hidden">
                    <button
                      className="w-full text-left p-6 flex justify-between items-center bg-transparent focus:outline-none"
                      onClick={() => toggleFAQ(index)}
                      aria-expanded={isOpen}
                    >
                      <h3 className="font-bold text-base sm:text-lg text-[#0B1C2D]">{faq.question}</h3>
                      <span className={`text-[#C6A25E] text-xl transition-transform duration-300 transform ${isOpen ? 'rotate-180' : 'rotate-0'}`}>
                        ▾
                      </span>
                    </button>

                    <div
                      className={`transition-all duration-300 ease-in-out ${isOpen ? 'max-h-60 opacity-100 border-t border-slate-200/30' : 'max-h-0 opacity-0 pointer-events-none'
                        }`}
                    >
                      <div className="p-6 text-slate-600 text-sm leading-relaxed nm-inset-light">
                        {typeof faq.answer === 'object' ? (
                          <div>
                            <p className="mb-2 font-semibold">{faq.answer.text}</p>
                            <ul className="list-disc pl-5 space-y-1">
                              {faq.answer.listItems.map((item, idx) => (
                                <li key={idx}>{item}</li>
                              ))}
                            </ul>
                          </div>
                        ) : (
                          <p>{faq.answer}</p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Unified footer & FABs */}
        <Footer />
        <FloatingActions />
      </div>
    </React.Fragment>
  );
}

// Junior Academy marquee dataset
const juniorMarquee = [
  {
    name: "Class 10th Boards",
    svg: (
      <svg className="w-8 h-8 text-[#C6A25E]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
      </svg>
    )
  },
  {
    name: "Intermediate MPC",
    svg: (
      <svg className="w-8 h-8 text-[#C6A25E]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 11h.01M12 7h.01M15 7h.01M15 11h.01M12 14h.01M15 14h.01M9 11h.01M9 7h.01M3 19V5a2 2 0 012-2h14a2 2 0 012 2v14a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
      </svg>
    )
  },
  {
    name: "Intermediate BiPC",
    svg: (
      <svg className="w-8 h-8 text-[#C6A25E]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
        <path strokeLinecap="round" strokeLinejoin="round" d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 11.172V5L8 4z" />
      </svg>
    )
  },
  {
    name: "EAPCET & NEET Prep",
    svg: (
      <svg className="w-8 h-8 text-[#C6A25E]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
      </svg>
    )
  },
  {
    name: "Concept-First Method",
    svg: (
      <svg className="w-8 h-8 text-[#C6A25E]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9.663 17h4.673M12 3v1m6.364 .364l-.707 .707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 113.536 0V21h2v-2.172a5 5 0 013.536-8.828" />
      </svg>
    )
  },
  {
    name: "24/7 Doubt Resolution",
    svg: (
      <svg className="w-8 h-8 text-[#C6A25E]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
        <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
      </svg>
    )
  },
  {
    name: "Mock Exam Series",
    svg: (
      <svg className="w-8 h-8 text-[#C6A25E]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    )
  },
  {
    name: "1-on-1 Mentorship",
    svg: (
      <svg className="w-8 h-8 text-[#C6A25E]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
      </svg>
    )
  }
];